/**
 * LabPdfPreviewPanel.tsx
 *
 * Inline PDF viewer that takes over the patient-detail right sidebar when a
 * lab document PDF is clicked in the Labs > Documents tab. Replaces the patient
 * details with the document and widens the panel. Provides "New Tab" (open the
 * PDF in a real browser tab, then revert the sidebar) and "Close" (just revert).
 */
import { useEffect, useRef, useState } from 'react';
import { ExternalLink, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { fetchLabReportViewUrl, fetchNoteAttachmentViewUrl, type LabViewUrl } from '@/services/labs.service';

/**
 * What to load into the patient-detail right sidebar from the Labs section:
 *  - PDF previews (Quest/Vibrant report or a note attachment), rendered by
 *    LabPdfPreviewPanel.
 *  - Structured lab detail (Quest/Vibrant result or a Vibrant outbound order),
 *    rendered by LabDetailPanel with an expand-to-modal affordance.
 */
export type LabPreviewTarget =
  | { kind: 'report'; source: string; id: number; title: string }
  | { kind: 'attachment'; patientId: number; attachmentId: number; title: string }
  | { kind: 'quest-detail'; labResultId: number; orderId?: number; title: string }
  | { kind: 'vibrant-detail'; labResultId: number; title: string }
  | { kind: 'vibrant-order-detail'; orderId: number; title: string }
  | { kind: 'access-detail'; labResultId: number; title: string };

interface LabPdfPreviewPanelProps {
  patientId: number;
  target: LabPreviewTarget;
  onClose: () => void;
  onNewTab: () => void;
}

export function LabPdfPreviewPanel({ patientId, target, onClose, onNewTab }: LabPdfPreviewPanelProps) {
  const [view, setView] = useState<LabViewUrl | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Holds the object URL so cleanup can revoke it without racing React state.
  const viewRef = useRef<LabViewUrl | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setView(null);

    const promise = target.kind === 'report'
      ? fetchLabReportViewUrl(target.source, target.id)
      : target.kind === 'attachment'
        ? fetchNoteAttachmentViewUrl(patientId, target.attachmentId)
        : Promise.reject(new Error('Unsupported preview target'));

    promise
      .then((resolved) => {
        if (cancelled) {
          if (resolved.revoke) URL.revokeObjectURL(resolved.url);
          return;
        }
        viewRef.current = resolved;
        setView(resolved);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Unable to load this document.');
        setLoading(false);
      });

    return () => {
      cancelled = true;
      if (viewRef.current?.revoke) URL.revokeObjectURL(viewRef.current.url);
      viewRef.current = null;
    };
  }, [patientId, target]);

  function handleNewTab() {
    if (view) {
      window.open(view.url, '_blank', 'noopener,noreferrer');
      // Defer revoke so the new tab can finish loading the blob. Detach from the
      // cleanup path so closing the panel doesn't kill the just-opened tab.
      if (view.revoke) {
        const url = view.url;
        viewRef.current = null;
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
    }
    onNewTab();
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col bg-white">
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-900" title={target.title}>
          {target.title}
        </h3>
        <div className="flex shrink-0 items-center gap-1">
          <Button size="sm" variant="secondary" onClick={handleNewTab} disabled={!view}>
            <ExternalLink className="mr-1 h-3.5 w-3.5" />
            New Tab
          </Button>
          <Button size="sm" variant="secondary" onClick={onClose}>
            <X className="mr-1 h-3.5 w-3.5" />
            Close
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 bg-slate-100">
        {loading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : error ? (
          <div className="flex h-full items-center justify-center px-6 text-center text-sm text-slate-500">
            {error}
          </div>
        ) : view ? (
          <iframe src={view.url} title={target.title} className="h-full w-full border-0" />
        ) : null}
      </div>
    </div>
  );
}
